'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '~/lib/utils';

const textVariants = cva('text-base text-foreground web:select-text font-sans', {
  variants: {
    variant: {
      default: '',
      secondary: 'text-text-secondary',
      muted: 'text-muted-foreground',
      error: 'text-destructive',
      link: 'text-link-primary hover:text-link-primary/80 transition-colors',
      linkSecondary: 'text-link-secondary hover:text-link-secondary/80 transition-colors',
      light: 'text-white',
      title: 'font-bold tracking-tight',
      balance: 'balance-text font-light',
      display: 'font-normal tracking-tight',
      heading: 'font-bold',
      subheading: 'font-normal',
      serif: 'font-bold tracking-tight',
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
      subheading: 'text-subheading',
    },
    weight: {
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    fontStyle: {
      normal: '',
      italic: 'italic',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'base',
    weight: 'normal',
    fontStyle: 'normal',
  },
});

const TextClassContext = React.createContext<string | undefined>(undefined);

interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?:
    | 'default'
    | 'secondary'
    | 'muted'
    | 'error'
    | 'link'
    | 'linkSecondary'
    | 'light'
    | 'title'
    | 'balance'
    | 'display'
    | 'heading'
    | 'subheading'
    | 'serif';
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'subheading';
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
  asChild?: boolean;
}

const Text = React.forwardRef<HTMLSpanElement, TextProps>(
  ({ className, variant, size, weight, fontStyle, asChild = false, ...props }, ref) => {
    const textClass = React.useContext(TextClassContext);
    const Component = asChild ? Slot : 'span';

    // Apply Libre Baskerville font for header variants
    const isSerif = variant === 'title' || variant === 'display' || variant === 'heading' || variant === 'subheading' || variant === 'serif';
    const serifClass = isSerif ? 'font-serif' : '';

    return (
      <Component
        className={cn(textVariants({ variant, size, weight, fontStyle }), serifClass, textClass, className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Text.displayName = 'Text';

export { Text, TextClassContext, textVariants };
